import '../App.css';
import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { binary_search, mapTypeAvailable } from '../util'
import { PlayerIntakeLostInterface } from '../types';

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

type GraphDataInterface = Array<{
  'x':  Array<string>,
  'y': Array<number|null>,
  'mode': string,
  'marker': {
    'color': string,
  },
  'name': string,
}>;
function PlayerIntakeLost(props: PlayerIntakeLostInterface) {
  const filterBar = props.filterBarValues;

  const [dataGraph, setDataGraph]= useState<GraphDataInterface>([]);

  useEffect(() => {
    var i;
    if(Object.keys(filterBar).length>0) {
      var processingStartTimeFirst = Date.now();

      // Setup game amount played graph

      // To be able to bin correctly we need to adjust the start date (oldest date)
      // to a multiple of time granularity. We take the end date (most recent)
      // as "the reference" and substract time granularities until we go pass the 
      // start date.

      // binsPeriod holds the period interval limits. The last one is the end date
      // selected in the interface. The first one is such that the start date
      // selected in the interface is completely captured at the current granularity
      // The first period will be incomplete and should not be displayed.
      var binsPeriodNumber = Math.ceil((filterBar.endDate - filterBar.startDate)/1000/(3600*24*filterBar.timeGranularity)) + 1;
      if(binsPeriodNumber<3) {
        // TODO: some error message
      };
      const binsPeriod = new Array(binsPeriodNumber).fill(0);
      var playersActiveBucket: Array<Set<number>> = [];

      for(i=0;i<binsPeriodNumber;i++) {
        playersActiveBucket.push(new Set())
        binsPeriod[binsPeriodNumber-1-i] = Math.round(filterBar.endDate/1000) - i*3600*24*filterBar.timeGranularity;
      };

      var graphDataX = [];
      var graphData = [];

      for(i=1;i<binsPeriodNumber;i++) {
        const d = new Date(binsPeriod[i]*1000);
        graphDataX.push(d.getFullYear()+"-"+(d.getMonth()+1)+"-"+d.getDate());
      };

      const jsonPlayersMin = binary_search(props.jsonPlayers.first_battle_sec, (Math.round(Date.now() / 1000) - 3600*24*filterBar.accountAgeRangeDays[1]), false, true);
      const jsonPlayersMax = binary_search(props.jsonPlayers.first_battle_sec, (Math.round(Date.now() / 1000) - 3600*24*filterBar.accountAgeRangeDays[0]), true, false);

      const player_id_min = props.jsonPlayers.player_id[jsonPlayersMin];
      const player_id_max = props.jsonPlayers.player_id[jsonPlayersMax];

      const jsonBattlesMin = binary_search(props.jsonBattles.started_sec, binsPeriod[0], false, true);
      const jsonBattlesMax = binary_search(props.jsonBattles.started_sec, binsPeriod[binsPeriodNumber-1], true, false);

      var gamesIds = filterBar.gamesChecked;
      

      for(i=jsonBattlesMin; i<=jsonBattlesMax; i++) {
        if(!gamesIds.includes(props.jsonBattles.game_id[i])) {
          continue;
        };

        var map_idx = binary_search(props.jsonMaps.map_id, props.jsonBattles.map_id[i]);

        if(!filterBar.mapTypeAvailableChecked.map((elem, j) =>
            (!elem || mapTypeAvailable[j]['value'] === props.jsonMaps[mapTypeAvailable[j]['column']][map_idx])).every((x)=>x)) {
              continue;
        }

        const battleBucketIdx = binary_search(binsPeriod, props.jsonBattles.started_sec[i], false, true);

        var battle_player_idx = binary_search(props.jsonBattlePlayer.battle_id, props.jsonBattles.battle_id[i], true, false, true);
        while(props.jsonBattlePlayer.battle_id[battle_player_idx] === props.jsonBattles.battle_id[i]) {

          // If accountAge filter does not include this player, skip
          if (props.jsonBattlePlayer.player_id[battle_player_idx] < player_id_min ||
            player_id_max < props.jsonBattlePlayer.player_id[battle_player_idx]) {
              battle_player_idx++;
              continue;
          }  
          playersActiveBucket[battleBucketIdx].add(props.jsonBattlePlayer.player_id[battle_player_idx]);
        	battle_player_idx++;
        }
      };

      // Process the sets of players

      const series: {[key: string]:{'color':string, 'values': Array<number | null>}} = {
        'Unique players': {
          'color': 'red',
          'values': new Array(binsPeriodNumber).fill(0),
        },
        'New players': {
          'color': 'coral',
          'values': new Array(binsPeriodNumber).fill(0),
        },
        'First time players': {
          'color': 'coral',
          'values': new Array(binsPeriodNumber).fill(0),
        },        
        'Leaving players': {
          'color': 'green',
          'values': new Array(binsPeriodNumber).fill(0),
        },
      }

      function difference(setA: Set<number>, setB: Set<number>) {
        const _difference = new Set(setA);
        for (const elem of setB) {
          _difference.delete(elem);
        }
        return _difference;
      }

      // The first bin is incomplete, hence should not be touched or used in computations
      series['New players'].values[1] = null;
      for(i=1;i<binsPeriodNumber;i++) {
        series['Unique players'].values[i] = playersActiveBucket[i].size;
        if (i>1) {
          series['New players'].values[i] = difference(playersActiveBucket[i], playersActiveBucket[i-1]).size;
        }
        if (i<binsPeriodNumber-1) {
          series['Leaving players'].values[i] = difference(playersActiveBucket[i], playersActiveBucket[i+1]).size;
        }

        var firstTime = 0;
        for (const playerId of playersActiveBucket[i]) {
          const playerIdx = binary_search(props.jsonPlayers.player_id, playerId);
          const firstLogin = props.jsonPlayers.first_battle_sec[playerIdx];
          if (binsPeriod[i-1]<firstLogin && firstLogin<binsPeriod[i]) {
            firstTime++;
          }
        }
        series['First time players'].values[i] = firstTime;
      }
      series['Leaving players'].values[binsPeriodNumber-1] = null;

      for(const game_type in series) {
        graphData.push({
          'x': graphDataX,
          'y': series[game_type].values.slice(1),
          'mode': 'lines+markers',
          'marker': {
            'color': series[game_type]['color'],
          },
          'name': game_type,
        });

      }
      console.log("INFO: complete time "+(Date.now()-processingStartTimeFirst));
      setDataGraph(graphData);
      return;
    }
  }, [props.filterBarValues, props.jsonBattles])

  var xaxisTitle = "";
  if (filterBar.timeGranularity === 1) {
    xaxisTitle = "Date"
  } else if (filterBar.timeGranularity === 7) {
    xaxisTitle = "Period of one week ending on date";
  } else if (filterBar.timeGranularity == 14) {
    xaxisTitle = "Period of two weeks ending on date";
  } else if (filterBar.timeGranularity === 30) {
    xaxisTitle = "Period of one month (30 days) ending on date";
  }

  const layoutLine : Partial<Plotly.Layout> = {
    autosize: true,
    title: 'Number of players by status',
    xaxis: {
      title: xaxisTitle,
      showgrid: false,
      zeroline: false
    },
    yaxis: {
      title: 'Number of players',
      showline: false
    },
    legend: {
      x: 0.1,
      y: -0.3,
      yanchor: 'top',
      orientation: "h"
    },
  };

  return (
    <div>
      <Stack spacing={2}>
        <Item>
          {props.children}
        </Item>
        <Item>
          <Typography>
            First time players are computed based on the time the players played their first battle.
          </Typography>
          <Typography>
            Leaving/new players are considered compared to the next and previous period. So, for example,
            if time granularity is one month, a player that plays every other month, will record as both 
            new player (because he did not play in previous month) and leaving player (because he did not
            play in the next months) in the months in which he actually plays. Leaving players will not
            be shown for last period, nor new players for the first period - because they can't be computed
            according to the above definition.
          </Typography>
        </Item>
        <Item>
          <Stack direction="row" spacing={1}>
            <Item style={{ width: "100%" }}>
              {dataGraph &&
                <Plot
                  data={dataGraph}
                  useResizeHandler={true}
                  style={{ width: "100%", height: "450px" }}
                  layout={layoutLine}
                  config={ {responsive: true} }
                />
              }
            </Item>
          </Stack>
        </Item>
      </Stack>
    </div>
  );
}

PlayerIntakeLost.propTypes = {
  jsonBattles: PropTypes.object,
  jsonBattlePlayer: PropTypes.object,
  jsonGames: PropTypes.object,
  jsonMaps: PropTypes.object,
  jsonPlayers: PropTypes.object,
  filterBarValues: PropTypes.object,
};

export default PlayerIntakeLost;
